// Staff profile-edit schema (BL-1170) — the Edit member modal's contract.
//
// One schema, both sides (rule #27 / ADR 016): `zodResolver` in
// `EditUserModal` and `parseBody` in `POST /api/user/update`.
//
// BL-1170 replaced the single free-text "Full name" box with captured
// `firstName` + `lastName`. Two deliberate asymmetries with the INVITE schema
// (`./invite.ts`, where BOTH names are required):
//
//   1. `lastName` is OPTIONAL here. Some staff go by one name, and BL-1170's
//      backfill leaves single-word legacy rows with no surname — requiring a
//      surname would make those rows uneditable until an operator invented one.
//      An invite is a fresh capture, so it can demand both.
//
//   2. There is NO `name` field, and since BUG-1650 there is no `name` COLUMN
//      to write either: the display name is DERIVED from the parts on read by
//      `resolveStaffDisplayName`, and the stored column Better Auth requires
//      was renamed `name_from_better_auth_do_not_use` and left to the library.
//      A client-settable display name is what let one drift from the parts —
//      which is how role labels ('Dan (Admin) Saper') ended up stamped into
//      invoice and settlement notes. The route still rejects a submitted
//      `name` outright.

import { z } from '@auctogon/core/zod';

import { addPhoneFormatIssue } from './phone-field';

/** DB-facing length caps for the `staff_users` name columns. */
const MAX = {
  firstName: 100,
  lastName: 100,
} as const;

/**
 * The staff profile patch accepted by `POST /api/user/update`.
 *
 * This is a PATCH contract: every field is optional and an omitted field is
 * left unchanged (the route also serves email-only and phone-only updates).
 * But `firstName` is non-blank-REQUIRED *when present* — `staff_users` IS the
 * Better Auth user table (rule #17), which requires a non-blank `name`, and
 * `name` is composed from these parts, so a whitespace-only first name must be
 * rejected rather than allowed to compose an empty display name.
 *
 * A blank surname normalizes to `null` (never `''`) because every read branches
 * on null to mean "no surname".
 *
 * The Edit member modal always sends `firstName`, so the client-side resolver
 * enforces presence through the form's own required field rather than here.
 */
// NOTE: `userId` and `tenantId` are deliberately NOT in this schema. The route
// gate resolves both before this schema runs (BL-1833 — `shared_membership`:
// the tenant acted in, and the target's membership of it), and the client form
// never submits either as a field.
/**
 * The shared field set, extracted (BUG-1650) so the Team-page contract below and
 * the self-service one further down derive from ONE declaration of what a staff
 * name part is. Not exported: callers take a schema, never the raw object.
 */
const staffProfileFields = z.object({
  firstName: z
    .string({ error: 'First name is required' })
    .trim()
    .min(1, 'First name is required')
    .max(MAX.firstName, `First name must be ${MAX.firstName} characters or fewer`)
    .optional(),
  lastName: z
    .string()
    .trim()
    .max(MAX.lastName, `Last name must be ${MAX.lastName} characters or fewer`)
    .optional()
    .nullable()
    .transform((v) => (v == null || v === '' ? null : v)),
  email: z.string().trim().email('Enter a valid email address').optional(),
  /** `null` clears the phone; `undefined` leaves it unchanged. */
  phone: z.string().trim().optional().nullable(),
});

export const StaffProfileUpdateSchema = staffProfileFields
  // BL-1277 — phone format. Patch-shaped: `undefined` (untouched) and `null`/''
  // (a deliberate clear) both pass through; only a submitted value is checked.
  .superRefine((val, ctx) => {
    addPhoneFormatIssue(val.phone, ctx, { path: ['phone'] });
  });

/** Parsed staff profile patch — the route's validated input (schema OUTPUT). */
export type StaffProfileUpdateInput = z.infer<typeof StaffProfileUpdateSchema>;

/**
 * The schema's INPUT shape — what a form holds before parsing.
 *
 * Distinct from {@link StaffProfileUpdateInput} because `lastName` carries a
 * `.transform()` (blank → `null`), so pre-parse values allow `undefined` where
 * the parsed output does not. React Hook Form needs the input type for its field
 * values and the output type for the submit handler, which is why both are
 * exported (`useForm<StaffProfileUpdateFormValues, unknown, StaffProfileUpdateInput>`).
 */
export type StaffProfileUpdateFormValues = z.input<typeof StaffProfileUpdateSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Self-service ("My Profile") — BUG-1650
//
// Derived from the schema above rather than re-declared, so the two paths
// cannot disagree about what a valid name part is. The difference is authority,
// not validation: the Team page's route edits ANOTHER member (and may change
// their email), while My Profile edits only yourself and must not reach email.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The patch accepted by `PATCH /api/user/profile` — the signed-in user editing
 * their own name and phone.
 *
 * `email` is omitted deliberately: a self-edit must not be able to move the
 * identity the session is keyed on. Everything else is inherited, including
 * "`firstName` is non-blank-required when present", so a blank name cannot be
 * saved here either.
 *
 * Still a PATCH: a phone-only save is a real edit (BUG-630's path), so the name
 * parts stay optional at the wire level. The FORM requires both — see
 * {@link StaffProfileFormSchema} — because it always renders and submits both.
 *
 * BUG-1650: this route used to accept a single composed `name` and write it
 * straight to the column Better Auth owns, leaving both parts NULL. A staff
 * member who renamed themselves here became invisible to the Edit member modal
 * (which reads the parts) and their internal notes were attributed to
 * "Unknown". Omitting `name` from the schema makes that body a 400 rather than
 * a silent 200 that writes the wrong column.
 */
export const StaffSelfProfilePatchSchema = staffProfileFields
  .omit({ email: true })
  // `.strict()` deliberately, and it is load-bearing: zod's default object mode
  // STRIPS an unknown key silently, so a stale client still sending the retired
  // composed `name` would get a 200 for a save that changed nothing about their
  // name — indistinguishable from the original defect. Strict turns that into a
  // 400 that names the field.
  .strict()
  .superRefine((val, ctx) => {
    addPhoneFormatIssue(val.phone, ctx, { path: ['phone'] });
    // Half a name is never a valid save: composing one part alone would store a
    // surname as somebody's whole display name. The Team-page schema does not
    // need this — it patches one field at a time from a modal that already
    // holds the other — but My Profile's form owns both inputs together.
    //
    // `lastName` is tested with `!= null`, not `!== undefined`: its
    // `.transform()` sits OUTSIDE the `.optional()`, so an ABSENT key parses to
    // `null` rather than `undefined`. Reading that as "a last name was sent"
    // made a phone-only save 400 — the exact BUG-630 path RAC 3 protects.
    const sentFirst = val.firstName !== undefined;
    const sentLast = val.lastName != null;
    if (sentFirst && !sentLast) {
      ctx.addIssue({ code: 'custom', path: ['lastName'], message: 'Last name is required' });
    }
    if (sentLast && !sentFirst) {
      ctx.addIssue({ code: 'custom', path: ['firstName'], message: 'First name is required' });
    }
    // An empty patch is a phantom write: it would bump `updated_at` and emit an
    // audit row for a save that changed nothing. Rejecting it here preserves the
    // route's original "No fields to update" 400 now that the check lives in the
    // schema rather than in hand-rolled route code.
    if (!sentFirst && !sentLast && val.phone === undefined) {
      ctx.addIssue({ code: 'custom', path: [], message: 'No changes to save' });
    }
  });

/** Parsed self-service profile patch — the route's validated input. */
export type StaffSelfProfilePatch = z.infer<typeof StaffSelfProfilePatchSchema>;

/**
 * The My Profile FORM's schema — both name parts REQUIRED.
 *
 * Deliberately stricter than the wire contract above. The form renders two
 * inputs and always submits them, so it has no way to express "leave my name
 * alone"; making the pair required is what stops an operator clearing their own
 * name back to the nameless state this bug is about. `lastName` is required
 * here even though the Team-page schema treats it as optional — a self-edit is
 * a person typing their own name, and they know both halves.
 */
export const StaffProfileFormSchema = z.object({
  firstName: z
    .string({ error: 'First name is required' })
    .trim()
    .min(1, 'First name is required')
    .max(MAX.firstName, `First name must be ${MAX.firstName} characters or fewer`),
  lastName: z
    .string({ error: 'Last name is required' })
    .trim()
    .min(1, 'Last name is required')
    .max(MAX.lastName, `Last name must be ${MAX.lastName} characters or fewer`),
  phone: z.string().trim().optional().nullable(),
}).superRefine((val, ctx) => {
  addPhoneFormatIssue(val.phone, ctx, { path: ['phone'] });
});

/** The My Profile form's values. */
export type StaffProfileFormValues = z.input<typeof StaffProfileFormSchema>;
